<template>
  <div class="sync-task-progress">
    <div v-if="task">
      <v-row dense align="center" class="mb-2">
        <v-col cols="auto">
          <v-chip small :color="taskStatusColor(task.status)" text-color="white">
            {{ $t('proforma-invoice.sync-task.statuses.' + task.status) }}
          </v-chip>
        </v-col>
        <v-col cols="auto">
          <span class="text--secondary">{{ $tc('organization.organization', 1) }} :</span>
          <router-link :to="{name: 'organizations.detail', params: {id: task.organizationId}}" class="font-weight-medium">
            {{ task.organizationName || task.organizationId }}
          </router-link>
        </v-col>
        <v-col cols="auto">
          <span class="text--secondary">{{ $t('proforma-invoice.sync-task.period') }} :</span>
          {{ task.month ? $t('proforma-invoice.sync-task.from-month', {month: task.month}) : $t('proforma-invoice.sync-task.all-history') }}
          <span v-if="task.deleteNotInvoicedProformaInvoice" class="warning--text">
            · {{ $t('proforma-invoice.sync-task.with-delete') }}
          </span>
        </v-col>
        <v-col cols="auto">
          <span class="text--secondary">{{ $t('proforma-invoice.sync-task.triggered-by') }} :</span>
          {{ task.triggeredByUserName || '-' }},
          {{ task.createdAt | formatDate($t('format_datetime')) }}
        </v-col>
        <v-spacer />
        <v-col cols="auto">
          <span class="font-weight-medium">{{ processed }} / {{ counters.total }}</span>
        </v-col>
      </v-row>

      <v-progress-linear
          :value="progressPercent"
          :indeterminate="isProcessing && counters.total === 0"
          :color="taskStatusColor(task.status)"
          height="6"
          class="mb-3"
      />

      <v-alert v-if="current" type="info" text dense icon="mdi-sync" class="mb-3">
        {{ $t('proforma-invoice.sync-task.current', {name: current.organizationName || current.organizationId}) }}
      </v-alert>

      <v-row dense class="mb-3" align="center">
        <v-col cols="auto" v-if="counters.done > 0">
          <v-chip x-small color="success" text-color="white">
            <v-icon left x-small>mdi-check</v-icon>
            {{ counters.done }} {{ $t('proforma-invoice.sync-task.item-statuses.DONE') }}
          </v-chip>
        </v-col>
        <v-col cols="auto" v-if="counters.failed > 0">
          <v-chip x-small color="error" text-color="white">
            <v-icon left x-small>mdi-alert-circle-outline</v-icon>
            {{ counters.failed }} {{ $t('proforma-invoice.sync-task.item-statuses.FAILED') }}
          </v-chip>
        </v-col>
        <v-col cols="auto" v-if="counters.pending > 0">
          <v-chip x-small color="grey lighten-1" text-color="white">
            <v-icon left x-small>mdi-clock-outline</v-icon>
            {{ counters.pending }} {{ $t('proforma-invoice.sync-task.item-statuses.PENDING') }}
          </v-chip>
        </v-col>
        <v-spacer />
        <v-col cols="auto" v-if="counters.failed > 0">
          <v-switch v-model="onlyFailed" :label="$t('proforma-invoice.sync-task.only-failed')" dense hide-details class="mt-0" />
        </v-col>
      </v-row>

      <v-data-table
          :items="displayedItems"
          :headers="headers"
          item-key="organizationId"
          :items-per-page="50"
          :footer-props="{ 'items-per-page-options': [50, 100, -1] }"
          dense
      >
        <template #item.organizationName="{item}">
          <router-link :to="{name: 'organizations.detail', params: {id: item.organizationId}}" class="text-decoration-none">
            {{ item.organizationName || item.organizationId }}
          </router-link>
        </template>

        <template #item.status="{item}">
          <v-chip x-small :color="itemStatusColor(item.status)" text-color="white">
            {{ $t('proforma-invoice.sync-task.item-statuses.' + item.status) }}
          </v-chip>
        </template>

        <template #item.message="{item}">
          <span v-if="item.status === 'FAILED'" class="error--text">{{ item.message || item.messageKey }}</span>
          <span v-else>-</span>
        </template>

        <template #item.processedAt="{item}">
          {{ item.processedAt ? formatDate(item.processedAt, $t('format_datetime')) : '-' }}
        </template>
      </v-data-table>
    </div>

    <div v-else-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" />
    </div>
  </div>
</template>

<script>
import ProformaInvoiceSyncTaskRepository from "@repository/ProformaInvoiceSyncTaskRepository"

const FINAL_STATUSES = ['DONE', 'FAILED']

export default {
  name: 'SyncTaskProgress',

  props: {
    taskId: { type: String, required: true },
    live: { type: Boolean, default: false },
    pollInterval: { type: Number, default: 2500 },
  },

  data() {
    return {
      task: null,
      loading: false,
      pollTimer: null,
      destroyed: false,
      onlyFailed: false,
    }
  },

  computed: {
    counters() {
      return this.task?.counters || { total: 0, done: 0, failed: 0, pending: 0 }
    },

    processed() {
      return (this.counters.done || 0) + (this.counters.failed || 0)
    },

    progressPercent() {
      return this.counters.total ? Math.round((this.processed / this.counters.total) * 100) : 0
    },

    isProcessing() {
      return this.task && !FINAL_STATUSES.includes(this.task.status)
    },

    current() {
      return (this.task?.items || []).find(item => item.status === 'IN_PROGRESS') || null
    },

    displayedItems() {
      const items = this.task?.items || []
      return this.onlyFailed ? items.filter(item => item.status === 'FAILED') : items
    },

    headers() {
      return [
        { text: this.$tc('organization.organization', 1), value: 'organizationName', sortable: false },
        { text: this.$t('status'), value: 'status', sortable: false, width: 140 },
        { text: this.$t('proforma-invoice.sync-task.message'), value: 'message', sortable: false },
        { text: this.$t('proforma-invoice.sync-task.processed-at'), value: 'processedAt', sortable: false, width: 180 },
      ]
    },
  },

  watch: {
    taskId() {
      this.task = null
      this.fetch()
    },
  },

  mounted() {
    this.fetch()
  },

  beforeDestroy() {
    this.destroyed = true
    if (this.pollTimer) {
      clearTimeout(this.pollTimer)
      this.pollTimer = null
    }
  },

  methods: {
    fetch() {
      if (!this.taskId) return
      this.loading = !this.task
      ProformaInvoiceSyncTaskRepository.get(this.taskId).then(data => {
        if (this.destroyed) return
        this.task = data
        this.loading = false
        this.$emit('task-updated', data)
        if (this.live && this.isProcessing) {
          this.scheduleNextPoll()
        } else if (this.live) {
          this.$emit('task-finished', data)
        }
      }).catch(err => {
        this.loading = false
        if (this.destroyed) return
        this.notifyError(err)
      })
    },

    scheduleNextPoll() {
      if (this.pollTimer) clearTimeout(this.pollTimer)
      this.pollTimer = setTimeout(() => {
        this.pollTimer = null
        this.fetch()
      }, this.pollInterval)
    },

    formatDate(value, fmt) {
      if (!value) return '-'
      return this.$options.filters.formatDate(value, fmt)
    },

    taskStatusColor(status) {
      switch (status) {
        case 'DONE': return 'success'
        case 'FAILED': return 'error'
        case 'IN_PROGRESS': return 'primary'
        default: return 'grey'
      }
    },

    itemStatusColor(status) {
      switch (status) {
        case 'DONE': return 'success'
        case 'FAILED': return 'error'
        case 'IN_PROGRESS': return 'primary'
        default: return 'grey lighten-1'
      }
    },
  },
}
</script>
